<template>
  <OnBoardingFrame
    as-form
    back
    :step="inSignupWizard ? 1 : 0"
    :title="$t('on_boarding.email_sign_up_details_page.create_your_account')"
    :error="errorMessage"
    @back="backTapped"
    @submit="signUp"
  >
    <!-- An interruption signup ("like", "add to list") runs no profile steps afterwards,
         so the step bar only shows on a deliberate signup. -->
    <div class="ob-fields">
      <div class="ob-field">
        <label class="ob-label" for="signupEmail">{{ $t('shared.email') }}</label>
        <input
          id="signupEmail"
          v-model.trim="email"
          type="email"
          class="ob-input"
          placeholder="example@email.com"
          autocomplete="email"
          required
        >
      </div>

      <!-- Single password field with show/hide toggle. We've intentionally
           dropped the confirm-password field — show/hide solves the typo
           problem with less friction. -->
      <div class="ob-field">
        <label class="ob-label" for="signupPassword">{{ $t('on_boarding.email_sign_up_details_page.password') }}</label>
        <div class="ob-password">
          <input
            id="signupPassword"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            class="ob-input"
            :placeholder="$t('on_boarding.email_sign_up_details_page.at_least_6_characters')"
            autocomplete="new-password"
            minlength="6"
            required
          >
          <button
            type="button"
            class="ob-icon-btn"
            :aria-label="showPassword ? $t('on_boarding.email_sign_up_details_page.hide_password') : $t('on_boarding.email_sign_up_details_page.show_password')"
            @click="showPassword = !showPassword"
          >
            <span class="ms fill" aria-hidden="true">{{ showPassword ? 'visibility_off' : 'visibility' }}</span>
          </button>
        </div>
      </div>
    </div>

    <template #footer>
      <button type="submit" class="ob-btn ob-btn-primary" :disabled="isLoading || !canSubmit">
        <template v-if="isLoading">
          <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          <span>{{ $t('on_boarding.email_sign_up_details_page.creating_account') }}</span>
        </template>
        <template v-else>{{ $t('on_boarding.email_sign_up_details_page.create_account') }}</template>
      </button>
    </template>
  </OnBoardingFrame>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import OnBoardingFrame from './OnBoardingFrame.vue';

export default defineComponent({
  name: 'OnBoardingEmailSignUpDetailsPage',
  components: { OnBoardingFrame },
  data() {
    return {
      email: '',
      password: '',
      showPassword: false,
    };
  },
  computed: {
    // An interruption flow (a "like" or "add to list" signup) reaches this form
    // too, and runs no profile steps afterwards — so there is no progress to
    // show there.
    inSignupWizard(): boolean {
      return this.$store.getters['onBoarding/isDeliberateSignup'];
    },
    isLoading(): boolean {
      return this.$store.state.onBoarding.isLoading;
    },
    errorMessage(): string | null {
      return this.$store.state.onBoarding.errorMessage;
    },
    canSubmit(): boolean {
      return this.email.length > 0 && this.password.length >= 6;
    },
  },
  methods: {
    backTapped() {
      this.$store.commit('onBoarding/setError', null);
      this.$store.commit('onBoarding/popPage');
    },
    async signUp() {
      if (!this.canSubmit) {
        this.$store.commit('onBoarding/setError', this.$t('on_boarding.email_sign_up_details_page.fill_in_all_fields'));
        return;
      }
      // No name here any more — the profile steps ask for first and last name
      // immediately after, matching the mobile flow (email → password → name).
      await this.$store.dispatch('onBoarding/signUpWithEmail', {
        email: this.email,
        password: this.password,
      });
    },
  },
  beforeUnmount() {
    this.$store.commit('onBoarding/setError', null);
  },
});
</script>
